"use server";

import { ObjectId } from "mongodb";
import { revalidatePath } from "next/cache";
import { redirect } from "next/navigation";
import { cookies, headers } from "next/headers";
import { randomBytes } from "node:crypto";
import { getDb } from "../mongo";
import { sendEmail, appUrl, resetEmail } from "../email";
import { getRealUser, COOKIE_UID, COOKIE_VIEW_AS } from "../session";
import { signSession, SESSION_MAX_AGE_SECONDS } from "../session-token";
import { hashPassword, verifyPassword, needsRehash } from "../password";
import { checkRateLimit, rateLimitKey } from "../rate-limit";
import { parse } from "../validate";
import {
  loginSchema,
  acceptInviteSchema,
  requestPasswordResetSchema,
  resetPasswordSchema,
  changePasswordSchema,
  startViewAsSchema,
} from "../schemas/auth";
import { ActionResult, EMAIL_RE, normTeamRole, actionError } from "./shared";
import type { UserDoc, InviteDoc, ProjectDoc, PasswordResetDoc } from "../db-types";

/** Authenticate by email + password. Sets the session cookie and redirects on
 * success. Returns { ok:false, error } on bad credentials. */
export async function loginAction(formData: FormData): Promise<ActionResult> {
  const email = String(formData.get("email") ?? "").trim().toLowerCase();
  const password = String(formData.get("password") ?? "");
  if (!email || !password) return { ok: false, error: "Vyplň email a heslo" };
  const v = parse(loginSchema, { email, password });
  if (!v.ok) return { ok: false, error: v.error };
  const ip = (await headers()).get("x-forwarded-for")?.split(",")[0]?.trim() || "unknown";
  const rl = await checkRateLimit(rateLimitKey("login", ip, v.data.email), 5, 900);
  if (!rl.ok) return { ok: false, error: `Príliš veľa pokusov. Skús to o ${rl.retryAfter}s.` };
  try {
    const db = await getDb();
    const user = await db.collection<UserDoc>("users").findOne({ email: v.data.email });
    if (!user || typeof user.passwordHash !== "string") {
      return { ok: false, error: "Neplatné prihlasovacie údaje" };
    }
    const ok = await verifyPassword(user.passwordHash, password);
    if (!ok) return { ok: false, error: "Neplatné prihlasovacie údaje" };
    if (user.deletedAt || user.isActive === false) {
      return { ok: false, error: "Účet je deaktivovaný" };
    }
    if (needsRehash(user.passwordHash)) {
      await db
        .collection<UserDoc>("users")
        .updateOne({ _id: user._id }, { $set: { passwordHash: await hashPassword(password) } });
    }
    const jar = await cookies();
    jar.set(COOKIE_UID, await signSession(String(user._id)), {
      httpOnly: true,
      sameSite: "lax",
      path: "/",
      maxAge: SESSION_MAX_AGE_SECONDS,
    });
    // Clear any stale view-as cookie from a previous session
    jar.delete(COOKIE_VIEW_AS);
  } catch (e) {
    return actionError(e);
  }
  redirect("/");
}

/** Clear the session entirely (real cookie + any view-as override). */
export async function logout(): Promise<void> {
  const jar = await cookies();
  jar.delete(COOKIE_UID);
  jar.delete(COOKIE_VIEW_AS);
  redirect("/login");
}

/** Admin-only: start viewing the app as another user (typically a client). */
export async function startViewAs(targetUserId: string): Promise<ActionResult> {
  try {
    const v = parse(startViewAsSchema, { targetUserId });
    if (!v.ok) return { ok: false, error: v.error };
    const real = await getRealUser();
    if (!real || real.role !== "admin") return { ok: false, error: "Iba admin môže prepínať pohľad" };
    if (!ObjectId.isValid(targetUserId)) return { ok: false, error: "Neplatný účet" };
    if (targetUserId === real.id) return { ok: false, error: "To je tvoj vlastný účet" };
    const db = await getDb();
    const target = await db.collection<UserDoc>("users").findOne({ _id: new ObjectId(targetUserId) });
    if (!target) return { ok: false, error: "Účet neexistuje" };
    const jar = await cookies();
    jar.set(COOKIE_VIEW_AS, await signSession(targetUserId), {
      httpOnly: true,
      sameSite: "lax",
      path: "/",
      maxAge: 60 * 60 * 24,
    });
    revalidatePath("/", "layout");
    return { ok: true, id: targetUserId };
  } catch (e) {
    return actionError(e);
  }
}

/** Stop the view-as override and return to the real account. */
export async function stopViewAs(): Promise<ActionResult> {
  const jar = await cookies();
  jar.delete(COOKIE_VIEW_AS);
  revalidatePath("/", "layout");
  return { ok: true };
}

/**
 * Public (no session): accept an invite — sets the password, creates the
 * account, marks the invite used and signs the new user in.
 */
export async function acceptInvite(token: string, password: string): Promise<ActionResult> {
  let created = false;
  try {
    const v = parse(acceptInviteSchema, { token, password });
    if (!v.ok) return { ok: false, error: v.error };
    if (!token || token.length < 16 || token.length > 128) {
      return { ok: false, error: "Neplatná pozvánka" };
    }
    if (typeof password !== "string" || password.length < 8) {
      return { ok: false, error: "Heslo musí mať aspoň 8 znakov" };
    }

    const db = await getDb();
    const now = new Date();
    const invite = await db.collection<InviteDoc>("invites").findOne({
      token,
      acceptedAt: null,
      revokedAt: null,
      expiresAt: { $gt: now },
    });
    if (!invite) return { ok: false, error: "Pozvánka je neplatná alebo expirovaná" };

    const email = String(invite.email ?? "").toLowerCase();
    const dupe = await db.collection<UserDoc>("users").findOne({ email, deletedAt: null });
    if (dupe) return { ok: false, error: "Účet s týmto emailom už existuje" };

    const passwordHash = await hashPassword(password);
    const role = normTeamRole(invite.role) ?? "member";
    const inviteSections = role !== "admin" && Array.isArray(invite.sections)
      ? (invite.sections as unknown[]).map(String)
      : null;
    const res = await db.collection<UserDoc>("users").insertOne({
      name: String(invite.name ?? ""),
      email,
      role,
      clientId: role === "client" && invite.clientId ? invite.clientId : null,
      sections: inviteSections,
      passwordHash,
      isActive: true,
      deletedAt: null,
      createdAt: now,
      updatedAt: now,
      prefs: { theme: "light", sidebarCollapsed: false, tutorialCompletedAt: null, locale: "sk" },
    });

    if (role === "member" && Array.isArray(invite.projectIds) && invite.projectIds.length) {
      await db.collection<ProjectDoc>("projects").updateMany(
        { _id: { $in: invite.projectIds as ObjectId[] }, deletedAt: null },
        { $addToSet: { members: res.insertedId } },
      );
    }

    await db
      .collection<InviteDoc>("invites")
      .updateOne(
        { _id: invite._id },
        { $set: { acceptedAt: now, acceptedUserId: res.insertedId } },
      );

    const jar = await cookies();
    jar.set(COOKIE_UID, await signSession(String(res.insertedId)), {
      httpOnly: true,
      sameSite: "lax",
      path: "/",
      maxAge: SESSION_MAX_AGE_SECONDS,
    });
    jar.delete(COOKIE_VIEW_AS);
    created = true;
  } catch (e) {
    return actionError(e);
  }
  if (created) redirect("/");
  return { ok: true };
}

/**
 * Request a password reset. Always returns ok (never reveals whether the email
 * exists). If a matching active account is found, a one-hour token is stored
 * and a reset link is emailed.
 */
export async function requestPasswordReset(email: string): Promise<{ ok: boolean }> {
  const e = String(email ?? "").trim().toLowerCase();
  if (!EMAIL_RE.test(e)) return { ok: true };
  const v = parse(requestPasswordResetSchema, { email: e });
  if (!v.ok) return { ok: true };
  const ip = (await headers()).get("x-forwarded-for")?.split(",")[0]?.trim() || "unknown";
  const rl = await checkRateLimit(rateLimitKey("pwreset", ip, e), 5, 900);
  if (!rl.ok) return { ok: true };
  try {
    const db = await getDb();
    const user = await db.collection<UserDoc>("users").findOne({ email: e, deletedAt: null });
    if (user && user.isActive !== false && typeof user.passwordHash === "string") {
      const token = randomBytes(32).toString("hex");
      const now = new Date();
      await db.collection<PasswordResetDoc>("passwordResets").insertOne({
        token,
        userId: user._id!,
        email: e,
        createdAt: now,
        expiresAt: new Date(now.getTime() + 60 * 60 * 1000),
        usedAt: null,
      });
      const url = `${appUrl()}/reset/${token}`;
      const mail = resetEmail({
        name: user.name || "",
        url,
        locale: user.prefs?.locale,
      });
      await sendEmail({ to: e, subject: mail.subject, html: mail.html });
    }
  } catch (err) {
    console.error("[reset] request failed:", err);
  }
  return { ok: true };
}

/** Consume a reset token and set a new password. */
export async function resetPassword(token: string, newPassword: string): Promise<ActionResult> {
  const t = String(token ?? "");
  const pw = String(newPassword ?? "");
  if (!t) return { ok: false, error: "Neplatný odkaz" };
  if (pw.length < 8) return { ok: false, error: "Heslo musí mať aspoň 8 znakov" };
  const v = parse(resetPasswordSchema, { token: t, newPassword: pw });
  if (!v.ok) return { ok: false, error: v.error };
  const ip = (await headers()).get("x-forwarded-for")?.split(",")[0]?.trim() || "unknown";
  const rl = await checkRateLimit(rateLimitKey("pwreset-confirm", ip), 10, 900);
  if (!rl.ok) return { ok: false, error: `Príliš veľa pokusov. Skús to o ${rl.retryAfter}s.` };
  try {
    const db = await getDb();
    const rec = await db.collection<PasswordResetDoc>("passwordResets").findOne({ token: t, usedAt: null });
    if (!rec || !rec.expiresAt || new Date(rec.expiresAt) < new Date()) {
      return { ok: false, error: "Odkaz je neplatný alebo expirovaný" };
    }
    const hash = await hashPassword(pw);
    await db.collection<UserDoc>("users").updateOne(
      { _id: rec.userId },
      { $set: { passwordHash: hash, updatedAt: new Date() } },
    );
    // Burn this token + any other outstanding tokens for the same user.
    await db.collection<PasswordResetDoc>("passwordResets").updateMany(
      { userId: rec.userId, usedAt: null },
      { $set: { usedAt: new Date() } },
    );
    return { ok: true };
  } catch (e) {
    return actionError(e);
  }
}

/** Change the REAL user's password. Requires current password as confirmation. */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<ActionResult> {
  try {
    const v = parse(changePasswordSchema, { currentPassword, newPassword });
    if (!v.ok) return { ok: false, error: v.error };
    const real = await getRealUser();
    if (!real) return { ok: false, error: "Nie si prihlásený" };
    if (!currentPassword || !newPassword) return { ok: false, error: "Vyplň obe heslá" };
    if (newPassword.length < 6) return { ok: false, error: "Nové heslo musí mať aspoň 6 znakov" };
    if (newPassword.length > 128) return { ok: false, error: "Heslo je príliš dlhé" };

    const db = await getDb();
    const userDoc = await db.collection<UserDoc>("users").findOne({ _id: new ObjectId(real.id) });
    if (!userDoc || typeof userDoc.passwordHash !== "string") {
      return { ok: false, error: "Účet bez hesla" };
    }
    const matches = await verifyPassword(userDoc.passwordHash, currentPassword);
    if (!matches) return { ok: false, error: "Súčasné heslo je nesprávne" };

    const newHash = await hashPassword(newPassword);
    await db.collection<UserDoc>("users").updateOne(
      { _id: new ObjectId(real.id) },
      { $set: { passwordHash: newHash, updatedAt: new Date() } },
    );
    return { ok: true };
  } catch (e) {
    return actionError(e);
  }
}
